import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl) throw new Error('VITE_SUPABASE_URL is required');
if (!supabaseAnonKey) throw new Error('VITE_SUPABASE_ANON_KEY is required');

// Singleton pattern for Supabase client
class SupabaseClientManager {
  private static instance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'app.auth.token',
      storage: window.localStorage,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    global: {
      headers: {
        'x-client-info': 'ai-prime-consulting@1.0.0'
      }
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  });

  static getClient() {
    return this.instance;
  }

  static async reset() {
    try {
      // First clear auth state
      await this.instance.auth.signOut();
      
      // Clear all Supabase-related storage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('app.auth.') || key.startsWith('supabase.')) {
          localStorage.removeItem(key);
        }
      });

      // Create new client instance with refreshed configuration
      this.instance = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          storageKey: 'app.auth.token',
          storage: window.localStorage,
          detectSessionInUrl: true,
          flowType: 'pkce'
        },
        global: {
          headers: {
            'x-client-info': 'ai-prime-consulting@1.0.0'
          }
        },
        realtime: {
          params: {
            eventsPerSecond: 10
          }
        }
      });

      return this.instance;
    } catch (error) {
      console.error('Error during Supabase reset:', error);
      throw error;
    }
  }

  // Create user with proper error handling and relationship management
  static async createUser(email: string, password: string, organizationId?: string) {
    try {
      // First check if user exists in auth.users
      const { data: authData, error: signUpError } = await this.instance.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          // Disable auto sign in after creation
          data: { 
            email_confirm_required: true
          }
        }
      });

      if (signUpError) {
        throw signUpError;
      }

      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

      // Wait briefly to ensure auth user is created
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create organization association if specified
      if (organizationId) {
        const { error: orgError } = await this.instance
          .from('organization_users')
          .insert({
            user_id: authData.user.id,
            organization_id: organizationId,
            role: 'user'
          });

        if (orgError) {
          console.error('Error creating organization association:', orgError);
          // Don't throw - the user is still created successfully
        }
      }

      return { user: authData.user, error: null };

    } catch (error) {
      console.error('Error in createUser:', error);
      return { 
        user: null, 
        error: error instanceof Error ? error.message : 'An unknown error occurred' 
      };
    }
  }

  // Retry failed requests
  static async retryRequest<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: Error | null = null;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        // Add a small delay before retrying, except for the first attempt
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i - 1)));
        }
        
        return await operation();
      } catch (error) {
        console.warn(`Request attempt ${i + 1}/${maxRetries} failed:`, error);
        lastError = error instanceof Error ? error : new Error('Unknown error');
        
        // If this is a network error, retry
        if (error instanceof TypeError && error.message.includes('fetch')) {
          continue;
        }
        
        // If this is a Supabase error, check if it's retryable
        if (error && typeof error === 'object' && 'code' in error) {
          const errorCode = (error as any).code;
          // Retry on connection errors, timeout errors, and rate limiting
          if (['ECONNREFUSED', 'ETIMEDOUT', '429'].includes(errorCode)) {
            continue;
          }
        }
        
        // For other errors, don't retry
        throw lastError;
      }
    }
    
    throw lastError || new Error('Maximum retries exceeded');
  }
}

export const supabase = SupabaseClientManager.getClient();
export const resetSupabase = SupabaseClientManager.reset.bind(SupabaseClientManager);
export const createUser = SupabaseClientManager.createUser.bind(SupabaseClientManager);
export const retryRequest = SupabaseClientManager.retryRequest.bind(SupabaseClientManager);